import React, { useState } from 'react';
import SEO from '@/components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus({ type: 'success', message: response.data.message });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit form. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO 
        title="Contact Us"
        description="Get in touch with NDE Training & Consultancy. Contact us for sustainable development consultancy, project management training, M&E support, and organizational capacity building services in Suriname."
        keywords="contact NDE Suriname, sustainable development consultancy contact, project management training inquiry, Hannaslustweg 177 Wanica"
        url="https://ndebusiness.com/contact"
      />
      <section 
        className="section-spacing text-white relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/contact-header.jpg)',
          minHeight: '305px'
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="content-container relative z-10">
          <h1 className="font-rubik text-4xl md:text-5xl font-bold text-center mb-4" data-testid="contact-title">
            GET IN TOUCH
          </h1>
          <p className="font-roboto text-lg text-center max-w-3xl mx-auto">
            We'd love to hear from you. Whether you have a question about our services,
            want to discuss a potential project, or just want to say hello, we're here to help.
          </p>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="content-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-rubik text-3xl font-bold mb-6 text-[#aaae10]">
                CONTACT INFORMATION
              </h2>
              <div className="space-y-6 font-roboto">
                <div>
                  <h3 className="font-rubik font-semibold text-lg mb-2 text-[#646464]">
                    ADDRESS
                  </h3>
                  <p className="text-base">
                    Hannaslustweg 177<br />
                    District Wanica<br />
                    Suriname
                  </p>
                </div>
                <div>
                  <h3 className="font-rubik font-semibold text-lg mb-2 text-[#646464]">
                    EMAIL
                  </h3>
                  <a
                    href="mailto:Ndetraining2023@gmail.com"
                    className="text-base text-[#aaae10] hover:text-[#646464] transition-colors"
                    data-testid="contact-email"
                  >
                    Ndetraining2023@gmail.com
                  </a>
                </div>
                <div>
                  <h3 className="font-rubik font-semibold text-lg mb-2 text-[#646464]">
                    WHATSAPP
                  </h3>
                  <a
                    href="https://wa.me/5978600350"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-[#aaae10] hover:text-[#646464] transition-colors"
                    data-testid="contact-whatsapp"
                  >
                    +597 860 0350
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-rubik text-3xl font-bold mb-6 text-[#aaae10]">
                SEND US A MESSAGE
              </h2>
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="mb-4">
                  <label htmlFor="name" className="block font-rubik font-medium mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded font-roboto focus:outline-none focus:border-[#aaae10]"
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="email" className="block font-rubik font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded font-roboto focus:outline-none focus:border-[#aaae10]"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="phone" className="block font-rubik font-medium mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded font-roboto focus:outline-none focus:border-[#aaae10]"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block font-rubik font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded font-roboto focus:outline-none focus:border-[#aaae10]"
                    data-testid="contact-message-input"
                  ></textarea>
                </div>

                {submitStatus && (
                  <div
                    className={`mb-4 p-4 rounded ${
                      submitStatus.type === 'success'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                    data-testid="contact-form-status"
                  >
                    {submitStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-[#f5f5f5]">
        <div className="content-container">
          <h2 className="font-rubik text-3xl font-bold mb-6 text-center text-[#aaae10]">
            OUR LOCATION
          </h2>
          <div className="w-full h-[450px] rounded-lg overflow-hidden" data-testid="google-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3974.4268953274823!2d-55.21752420000001!3d5.7670058!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8d09cbfcc97cb4fd%3A0x4c3415faaffd4d66!2sHannaslustweg%20177%2C%20Suriname!5e0!3m2!1sen!2s!4v1735959600000!5m2!1sen!2s"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="NDE Business Location - Hannaslustweg 177, Suriname"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
